import React from 'react';
import PropTypes from 'prop-types';
import { dynastyTheme } from '../../../../../services/colorService';

const C = dynastyTheme.tokens.colors;

/**
 * CardTopBar - Sticky top bar for MobilePlayerCard
 * 
 * Features:
 * - Back button (collapses to modal)
 * - Player name (centered)
 * - Menu button (⋮)
 * - Sticky positioning
 * - Height: 56px
 * 
 * Used in: MobilePlayerCard
 */

const CardTopBar = ({ onBack, onClose }) => {
  // Controls-only bar — back + close. The player NAME renders ONCE, in PChrome's
  // identity row below (no duplicate). Flat #0d0d14 surface continuous with
  // PChrome (no gradient/border) so the whole top reads as ONE surface.
  return (
    <div style={styles.topBar}>
      <button onClick={onBack} style={styles.backButton} aria-label="Go back to modal">
        <span style={styles.backArrow}>←</span>
        <span style={styles.backText}>Back</span>
      </button>
      <span style={styles.title}>Player Profile</span>
      <button onClick={onClose} style={styles.menuButton} aria-label="Close player card">
        <span style={styles.menuIcon}>×</span>
      </button>
    </div>
  );
};

CardTopBar.propTypes = {
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  topBar: {
    position: 'sticky',
    top: 0,
    left: 0,
    right: 0,
    minHeight: '44px',
    height: '44px',
    flexShrink: 0, // fixed top link in the card's flex column (never squished)
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 'env(safe-area-inset-top, 0px)',
    paddingBottom: '0px',
    paddingLeft: '12px',
    paddingRight: '12px',
    // Flat house dark — continuous with PChrome below (ONE surface, no gradient/
    // border discoloration).
    background: C.ground.screen,
    zIndex: 100,
  },

  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    background: 'transparent',
    border: 'none',
    color: C.gold.bright,
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'opacity 0.1s ease',
    minWidth: '80px',
  },

  backArrow: {
    fontSize: '20px',
    lineHeight: '1',
  },

  backText: {
    fontSize: '16px',
  },

  title: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    margin: 0,
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: C.coolGray[650],
    textAlign: 'center',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },

  menuButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    padding: 0,
    background: 'transparent',
    border: 'none',
    color: C.coolGray[400],
    fontSize: '32px',
    fontWeight: '300',
    cursor: 'pointer',
    transition: 'color 0.1s ease',
    minWidth: '80px',
    marginLeft: 'auto',
  },

  menuIcon: {
    lineHeight: '1',
    transform: 'translateY(-2px)',
  },
};

// Hover states (if needed for web)
if (typeof window !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    button:active {
      opacity: 0.6;
    }
  `;
  document.head.appendChild(styleSheet);
}

export default CardTopBar;
